import React from 'react';
import uuid from 'uuid-random';
import { useTranslate } from 'hooks/useTranslate';
import { styled } from '@mui/system';
import SortComponent from 'pages/Professions/components/Sort';
import { getLanguage } from 'translation';
import Typography from '@mui/material/Typography';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Slider from '@mui/material/Slider';
import Button from '@mui/material/Button';

const FiltersWrapper = styled('div')(() => ({
  paddingBottom: 20,
  paddingTop: 20,
  maxWidth: 320
}));

const FiltersTitle = styled(Typography)(() => ({
  fontWeight: 700,
  fontSize: 18,
  marginBottom: 10,
  padding: '9px 0'
}));

const FiltersSubTitle = styled(Typography)(() => ({
  marginBottom: 10,
  marginTop: '-20px'
}));

const FormGroupStyled = styled(FormGroup)(() => ({
  marginBottom: 20
}));

const FormControlLabelStyled = styled(FormControlLabel)(() => ({}));

const CheckboxStyled = styled(Checkbox)(({ theme }) => ({
  color: theme.palette.grey[200]
}));

const Line = styled('div')(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.primary.main}`,
  marginBottom: 20
}));

const SalaryWrapper = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  gap: 20,
  marginBottom: 20,
  '& > div': {
    marginBottom: 0
  }
}));

const SalaryTextWrapper = styled('div')(() => ({
  display: 'flex',
  gap: 120
}));

const StyledSlider = styled(Slider)(({ theme }) => ({
  width: '80%',
  color: theme.palette.primary.main,
  '& .MuiSlider-track': {
    backgroundColor: theme.palette.primary.main
  },
  '& .MuiSlider-thumb': {
    backgroundColor: theme.palette.primary.main
  }
}));

const ButtonStyled = styled(Button)(() => ({
  marginTop: 20
}));

const minPrice = 2000;
const maxPrice = 6000;

const Filters = ({ actions, filters, sort }) => {
  const t = useTranslate('Consultants');
  const [key, setKey] = React.useState(uuid());
  const lang = getLanguage().toUpperCase();

  const defaultSort = React.useMemo(() => {
    return Object.keys(sort).length ? sort : { experienceFrom: 'asc' };
  }, [sort]);

  const sortValues = React.useMemo(
    () => [
      {
        column: `experienceFrom`,
        direction: 'asc',
        name: t('ByExperience')
      },
      {
        column: `price`,
        direction: 'asc',
        name: t('ByPrice')
      },
      {
        column: `lastName${lang}`,
        direction: 'asc',
        name: t('ByName')
      }
    ],
    [t, lang]
  );

  const handleFiltersChange = React.useCallback(
    (newFilters) => {
      actions.clearData();
      actions.onFilterChange(
        {
          ...filters,
          ...newFilters
        },
        false
      );
    },
    [actions, filters]
  );

  const handleChangeCheckbox = React.useCallback(
    (name) => {
      handleFiltersChange({
        [name]: filters[name] ? false : true
      });
    },
    [handleFiltersChange, filters]
  );

  const renderCheckbox = (name, label) => (
    <FormGroupStyled key={name}>
      <FormControlLabelStyled
        control={<CheckboxStyled />}
        label={t(label)}
        checked={filters[name]}
        onChange={() => handleChangeCheckbox(name)}
      />
    </FormGroupStyled>
  );

  return (
    <FiltersWrapper key={key}>
      <SortComponent actions={actions} sort={defaultSort} sortValues={sortValues} />

      <FiltersTitle>{t('Services')}</FiltersTitle>

      {renderCheckbox('with_test', 'WithTest')}

      <Line />

      {renderCheckbox('without_test', 'WithoutTest')}

      <Line />

      {renderCheckbox('with_support', 'WithSupport')}

      <FiltersTitle>{t('PriceFilter')}</FiltersTitle>

      <FiltersSubTitle>{`(${t('WithTest').toLowerCase()})`}</FiltersSubTitle>

      <SalaryWrapper>
        <SalaryTextWrapper>
          <Typography>{filters?.price_min || minPrice}</Typography>
          <Typography>{filters?.price_max || maxPrice}</Typography>
        </SalaryTextWrapper>
        <StyledSlider
          value={[filters?.price_min || minPrice, filters?.price_max || maxPrice]}
          onChange={(_, newValue) =>
            handleFiltersChange({ price_min: newValue[0], price_max: newValue[1] })
          }
          min={minPrice}
          max={maxPrice}
          valueLabelDisplay="auto"
        />
      </SalaryWrapper>

      {renderCheckbox('offline_work', 'WorkOnLine')}

      <ButtonStyled
        variant="contained"
        color="primary"
        fullWidth={true}
        onClick={() => {
          actions.clearFilters();
          setTimeout(() => setKey(uuid()), 200);
        }}>
        {t('ClearFilters')}
      </ButtonStyled>
    </FiltersWrapper>
  );
};

export default Filters;
